import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog,
    handleClose,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";

import {
    Iep,
    IEPEditResource,
    IEPViewResource,
    Permission
} from "generated/openApi";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { IEPPageHeader } from "./IEPPageHeader";
import { IEPPageBody } from "./IEPPageBody";

const routeName = "iep";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

class OptionalParams {
    closeOnSave?: boolean = false;
}

export const iepPageRoute: PageRoute<Params, OptionalParams> = {
    handle: "IEP",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
    getOptionalParams: (params) => buildOptionalParams(params),
};

export type IEPContextValue = {
    onCreate?: (id: number) => void;
};

export const IEPContext = createContext<IEPContextValue>({} as IEPContextValue);

export const IEPPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params, optionalParams } = useParams(routeName, Params, OptionalParams);

    const [iep, setIep] = useState<Iep>({} as Iep);
    const [initIep, setInitIep] = useState<Iep>(iep);
    const [viewResource, setViewResource] = useState<IEPViewResource>({});
    const [editResource, setEditResource] = useState<IEPEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [iep] });
    const { validationErrors, setValidationErrors } = useValidationErrors();
    const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

    useEffect(() => {
        fetch(params.id);

        ApiService.iep.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.iep.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${iepPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        if (id > 0) {
            ApiService.iep.get(id).then((response) => {
                setIep(response);
                setInitIep(response);
                resetUnsavedChanges([response]);
            });
        } else {
            // For new
            setIep({} as Iep);
            setInitIep({} as Iep);
            resetUnsavedChanges([{} as Iep]);
        }
    };

    const pageHeader = (
        <IEPPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={iepPageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            iep={iep}
            setIep={setIep}
            initIep={initIep}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
            closeOnSave={closeOnSave}
        />
    );

    const pageBody = (
        <IEPPageBody
            iep={iep}
            setIep={setIep}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
        />
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};